import { Link } from "@tanstack/react-router";
import { ArrowRight, Check } from "lucide-react";
import { PulseMark } from "@/components/site/PulseMark";
import type { Locale } from "@/lib/i18n";

interface PulseBadgeProps {
  locale: Locale;
  variant?: "inline" | "card";
}

const COPY = {
  en: {
    inline: "Powered by PULSE, our execution engine",
    cardEyebrow: "Execution Engine",
    cardTitle: "Every plan includes PULSE",
    cardBody:
      "Our proprietary execution layer. Validated changes, staging previews, zero risk to your live store.",
    benefits: [
      "Changes ship in days, not weeks",
      "7 automated checks before staging",
      "You preview & approve every change",
    ],
    learn: "Learn how PULSE works",
  },
  es: {
    inline: "Impulsado por PULSE, nuestro motor de ejecución",
    cardEyebrow: "Motor de Ejecución",
    cardTitle: "Cada plan incluye PULSE",
    cardBody:
      "Nuestra capa de ejecución propietaria. Cambios validados, previews en staging, cero riesgo en tu tienda en vivo.",
    benefits: [
      "Cambios entregados en días, no semanas",
      "7 chequeos automáticos antes de staging",
      "Tú previsualizas y apruebas cada cambio",
    ],
    learn: "Conoce cómo funciona PULSE",
  },
} as const;

export function PulseBadge({ locale, variant = "inline" }: PulseBadgeProps) {
  const t = COPY[locale];
  const href = locale === "es" ? "/es/pulse" : "/pulse";

  if (variant === "inline") {
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
      >
        <PulseMark variant="icon" size="sm" />
        <span>{t.inline}</span>
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    );
  }

  return (
    <div className="rounded-3xl border border-border bg-card/50 p-8 sm:p-10">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            <PulseMark variant="icon" size="sm" />
            {t.cardEyebrow}
          </div>
          <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            {t.cardTitle}
          </h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
            {t.cardBody}
          </p>
          <Link
            to={href}
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-foreground transition-all hover:gap-3"
          >
            {t.learn}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <ul className="space-y-3 lg:col-span-6">
          {t.benefits.map((b) => (
            <li key={b} className="flex items-start gap-3 text-sm text-foreground">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
